import {Component, EventEmitter, Input, OnInit, Output} from "@angular/core";
import {TemplateItem} from "../../Model/Catalog/TemplateItem";
import {TemplateFieldsService} from "../../Services/templateFields.service";
import {WarrantyClass} from "../../Model/Catalog/WarrantyClass";
import {fieldNames} from "../../Model/FieldNames";
import {LoginService} from "../../Services/login.service";
import {ValidFeatureSystem} from "../../Model/ValidFeatureSystem";
import {HazmatClass} from "../../Model/Catalog/HazmatClass";
import {ClickEvent} from "devextreme/ui/button";
import {NextPimField} from "../../Model/Catalog/NextPimField";
import {ReorderEvent} from "devextreme/ui/sortable";
import {Column, ColumnButton} from "devextreme/ui/data_grid";
import {TranslateService} from "@ngx-translate/core";

type DropdownFieldSource = {
    id: string;
    display: string;
    translate?: string[],
    active?: boolean
};

@Component({
  selector: 'np-advanced-template-fieldlist',
  templateUrl: './advanced-template-fieldlist.component.html',
  styleUrls: ['./advanced-template-fieldlist.component.css'],
  standalone: false
})
export class AdvancedTemplateFieldListComponent implements OnInit {
  @Input({ required: true }) customerId!: string;
  @Input({ required: true }) templateItem!: TemplateItem;
  @Input({ required: true }) warrantyClasses!: WarrantyClass[];
  @Input({ required: true }) hazmatClasses!: HazmatClass[];
  @Input({ required: true }) validFeatureSystems!: ValidFeatureSystem[];
  
  @Output() templateItemChanged = new EventEmitter<TemplateItem>();
  
  public readonly columns: Array<Column>;

  activeTypeFieldSource: DropdownFieldSource[] ; 
    
  typeFieldSource: DropdownFieldSource[] = [
    { id: 'fixed', display: 'Festwert' },
    {
      id: 'standard',
      display: 'Produkt',
      translate: [
        fieldNames.descriptionShort,
        fieldNames.descriptionLong,
        fieldNames.descriptionLongWithoutHtml,
        fieldNames.manufacturerTypeDescription,
        fieldNames.remarks,
        fieldNames.manufacturerName
      ]
    },
      {id: 'texts', display: 'Texte'},
      {id: 'dpb', display: 'DPB', active: this.loginService.hasAddonDpbImport || this.loginService.hasAddonDpbExport},
    {
      id: 'mime',
      display: 'Medien',
      translate: [fieldNames.mimeAlt, fieldNames.mimeDescription]
    },
    {
      id: 'feature',
      display: 'Features',
      translate: [
        fieldNames.featureSystemGroupName,
        fieldNames.featureSystemGroupName,
        fieldNames.fName,
        fieldNames.fUnit,
        fieldNames.fValue
      ]
    },
    { id: 'orderdetail', display: 'OrderDetails' },
    { id: 'price', display: 'Preislisten' },
    { id: 'logistics', display: 'Logistik' },
    { id: 'reference', display: 'Referenzen' },
    { id: 'category', display: 'Kategorien' },
    { id: 'udx', display: 'UDX' },
    { id: 'seo', display: 'SEO - SEA' },
    { id: 'legal', display: 'Recht' },
    {
      id: 'wawi',
        display: this.loginService.wawiSettings?.name && this.loginService.wawiSettings?.name != '' ? this.loginService.wawiSettings?.name : 'Wawi',
        active: this.loginService.hasAddonWawiErp
    },
    { id: 'supplier', display: 'Lieferanten' },
      {id: 'shopware6', display: 'Shopware6', translate: [], active: this.loginService.hasAddonShopware6},
    {
      id: 'packing',
      display: 'Verpackungen',
        translate: [fieldNames.packingUnit],
        active: this.loginService.hasAddonPackaging
    },
      {id: 'kiprompt', display: 'KI-Prompt', translate: [], active: this.loginService.hasAddonKI_TEXTS},
      {id: 'scireum_sellsite', display: 'Scireum Sellsite', active: this.loginService.hasAddonScireumSellsite},
      {id: 'sellThroughFigures', display: 'Abverkaufszahlen', active: this.loginService.hasAddonBauvista},
      {id: 'displayItems', display: 'Display-Artikel', active: this.loginService.hasAddonBauvista},
  ];
  
  constructor(
    private translate: TranslateService,
    private loginService: LoginService,
    public fieldService: TemplateFieldsService) {
    this.columns = [
      {
        cellTemplate: 'cell',
        dataField: 'fieldType', // DevExtreme benötigt ein dataField für die Bearbeitung
        setCellValue: (newData, value, currentRowData) => {
          // Diese Funktion wird aufgerufen, wenn die Bearbeitung abgeschlossen ist
          // Da wir custom templates verwenden, müssen wir nichts tun
          // Die Werte werden bereits in den dx-text-box onChange-Events aktualisiert
        }        
      } as Column,
      {
        type: 'buttons',
          buttons: [        
            { 
              icon: 'trash',
              hint: this.translate.instant('Entfernen'),              
              onClick: (e) => this.onRemoveField(e.row.data, e)
            } as ColumnButton,
            {
              icon: 'add',
              hint: this.translate.instant('Feld hinzufügen'),
              onClick: (e) => this.onAddFieldAfter(e.row.data)
            } as ColumnButton
        ]
      } as Column
    ];
  }
  
  ngOnInit() {
    const customTabEntries = (this.loginService.wawiSettings?.customTabs ?? [])
      .filter(t => t.name)
      .map(t => ({ id: 'tab:' + t.id, display: t.name }) as DropdownFieldSource);
    this.activeTypeFieldSource = [...this.typeFieldSource.filter(x => x.active !== false), ...customTabEntries];
  }
  
  readonly itemChanged = () => {
    this.templateItemChanged.emit(this.templateItem);
  }

  readonly onGroupChanged = (pimField: NextPimField) => {
    if (pimField.fieldType === 'fixed') {
      pimField.field = '';
      pimField.elementKey = '-1';
      pimField.systemKey = '-1';
    }
    this.itemChanged();
  }

  readonly onAddNewField = (event: ClickEvent) => {
    this.templateItem.pimFields = [...this.templateItem.pimFields, new NextPimField('-1', '-1', '')];

    this.templateItemChanged.emit(this.templateItem);
  }

  readonly onAddFieldAfter = (pimField: NextPimField) => {
    const index = this.templateItem.pimFields.indexOf(pimField);
    const insertAt = index < 0 ? this.templateItem.pimFields.length : index + 1;

    this.templateItem.pimFields.splice(insertAt, 0, new NextPimField('-1', '-1', ''));
    this.templateItem.pimFields = [...this.templateItem.pimFields];

    this.templateItemChanged.emit(this.templateItem);
  }
  
  readonly onRemoveField = (pimField: NextPimField, event: ClickEvent) => {
    this.templateItem.pimFields.splice(this.templateItem.pimFields.indexOf(pimField), 1);
    this.templateItem.pimFields = [...this.templateItem.pimFields];

    this.templateItemChanged.emit(this.templateItem);
  }

  readonly onReorderFields = (e: ReorderEvent) => {
    var movedFields = this.templateItem.pimFields.splice(e.fromIndex, 1);
    
    this.templateItem.pimFields.splice(e.toIndex, 0, ...movedFields);    
    this.templateItem.pimFields = [...this.templateItem.pimFields];

    this.templateItemChanged.emit(this.templateItem);
  }
}